import React from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ContentList from './pages/ContentList';
import ContentEditor from './pages/ContentEditor';
import AdminPanel from './pages/AdminPanel';
import UserProfile from './pages/UserProfile';
import Notifications from './pages/Notifications';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import PAASAdminDashboard from './pages/PAASAdminDashboard';
import SaaSAdminDashboard from './pages/SaaSAdminDashboard';
import ClientAdminDashboard from './pages/ClientAdminDashboard';
import Integrations from './pages/Integrations';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminRegister from './pages/SuperAdminRegister';
import ResetPassword from './pages/ResetPassword';
import PAASAdminLogin from './pages/PAASAdminLogin';
import SaaSAdminLogin from './pages/SaaSAdminLogin';
import ClientAdminLogin from './pages/ClientAdminLogin';
import UserLogin from './pages/UserLogin';
import Billing from './pages/Billing';
import Credits from './pages/Credits';
import Devices from './pages/Devices';
import VerifyEmail from './pages/VerifyEmail';
import Onboarding from './pages/Onboarding';
import PackageManager from './pages/PackageManager';
import Settings from './pages/Settings';
import Navbar from './components/Navbar';
import Sidebar, { HeaderBar, TopBar } from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import DomainRouter from './components/DomainRouter';
import LandingPage from './pages/LandingPages/LandingPage';
import CultisgoLandingPage from './pages/LandingPages/cultisgo/CultisgoLandingPage';
import NevixaLandingPage from './pages/LandingPages/nevixa/NevixaLandingPage';
import { getLandingPageConfig, parseSubdomain, getRouteForDomainType, DOMAIN_TYPES } from './utils/domainResolver';

function App() {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { darkMode } = useSelector((state) => state.branding);
  const { tenantId, paasId, saasId } = useParams();

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const landingPageConfig = getLandingPageConfig(hostname);
  const parsedSubdomain = parseSubdomain(hostname);
  const subdomainType = landingPageConfig ? DOMAIN_TYPES.UNKNOWN : parsedSubdomain.type;

  const getLandingPage = () => {
    if (!landingPageConfig) return <LandingPage />;
    switch (landingPageConfig.key) {
      case 'cultisgo': return <CultisgoLandingPage branding={landingPageConfig.branding} />;
      case 'nevixa': return <NevixaLandingPage branding={landingPageConfig.branding} />;
      default: return <LandingPage />;
    }
  };
  
  const getTenantId = () => {
    if (tenantId) return tenantId;
    const session = JSON.parse(localStorage.getItem('tenantSession') || '{}');
    return session?.tenantId;
  };

  const currentTenantId = user?.role === 'client-admin' || user?.role === 'owner' ? getTenantId() : null;

  const getRootRoute = () => {
    if (landingPageConfig) return getLandingPage();
    if (!isAuthenticated && subdomainType !== DOMAIN_TYPES.UNKNOWN) {
      return <Navigate to={getRouteForDomainType(subdomainType)} />;
    }
    return getDashboardRoute();
  };

  const getDashboardRoute = () => {
    if (!isAuthenticated) return <Navigate to="/login" />;
    switch (user?.role) {
      case 'superadmin': return <Navigate to="/super-admin/dashboard" />;
      case 'admin':
      case 'paasadmin': return <Navigate to={paasId ? `/paas-admin/${paasId}` : '/paas-admin'} />;
      case 'saasadmin': return <Navigate to={saasId ? `/saas-admin/${saasId}` : '/saas-admin'} />;
      case 'client-admin':
      case 'owner': return <Navigate to={tenantId ? `/client-admin/${tenantId}` : '/client-admin'} />;
      default: return <Dashboard />;
    }
  };

  const isAdminRole = user?.role && ['superadmin', 'admin', 'saasadmin', 'client-admin', 'owner'].includes(user.role);
  const isPAASAdminRoute = location.pathname.includes('/paas-admin/');
  const isSAASAdminRoute = location.pathname.includes('/saas-admin/');
  const isClientAdminRoute = location.pathname.includes('/client-admin/');
  const isSuperAdminRoute = location.pathname.includes('/super-admin');

  const showGlobalNav = isAuthenticated && !isSuperAdminRoute && !isPAASAdminRoute && !isSAASAdminRoute && !isClientAdminRoute;

  return (
    <DomainRouter>
      <div className={`flex min-h-screen bg-gray-100 ${darkMode ? 'dark' : ''}`}>
        {showGlobalNav && <Sidebar />}

        <div className="flex-1 flex flex-col">
          {showGlobalNav && (
            <>
              <HeaderBar tenantId={currentTenantId} />
              <TopBar />
            </>
          )}

          <div className={showGlobalNav ? 'flex-1' : 'flex-1'}>
            <Routes>
              <Route path="/home" element={getLandingPage()} />
              <Route path="/landing" element={getLandingPage()} />
              <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />
              <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/" />} />
              <Route path="/auth/callback" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/onboarding" element={<Onboarding />} />

              <Route path="/super-admin/login" element={<SuperAdminLogin />} />
              <Route path="/super-admin/register" element={<SuperAdminRegister />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/paas-admin/login" element={<PAASAdminLogin />} />
              <Route path="/saas-admin/login" element={<SaaSAdminLogin />} />
              <Route path="/client-admin/login" element={<ClientAdminLogin />} />
              <Route path="/user/login" element={<UserLogin />} />

              <Route path="/super-admin" element={
                <PrivateRoute>
                  <SuperAdminDashboard />
                </PrivateRoute>
              } />
              <Route path="/super-admin/dashboard" element={
                <PrivateRoute>
                  <SuperAdminDashboard />
                </PrivateRoute>
              } />
              <Route path="/super-admin/platforms" element={
                <PrivateRoute>
                  <SuperAdminDashboard />
                </PrivateRoute>
              } />
              <Route path="/super-admin/platforms/create" element={
                <PrivateRoute>
                  <SuperAdminDashboard />
                </PrivateRoute>
              } />
              <Route path="/super-admin/admins" element={
                <PrivateRoute>
                  <SuperAdminDashboard />
                </PrivateRoute>
              } />

            {/* PAAS Admin - SaaS sub-routes first */}
              <Route path="/paas-admin/:paasId/saas/create" element={
                <PrivateRoute>
                  <PAASAdminDashboard view="create-saas" />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/saas/admins" element={
                <PrivateRoute>
                  <PAASAdminDashboard view="saas-admins" />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/saas" element={
                <PrivateRoute>
                  <PAASAdminDashboard view="all-saas" />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/packages" element={
                <PrivateRoute>
                  <PackageManager level="paas" />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/add-package" element={
                <PrivateRoute>
                  <PackageManager level="paas" view="add" />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/package-usage" element={
                <PrivateRoute>
                  <PackageManager level="paas" view="usage" />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/billing" element={
                <PrivateRoute>
                  <Billing />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/credits" element={
                <PrivateRoute>
                  <Credits />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId/devices" element={
                <PrivateRoute>
                  <Devices />
                </PrivateRoute>
              } />
              <Route path="/paas-admin/:paasId" element={
                <PrivateRoute>
                  <PAASAdminDashboard />
                </PrivateRoute>
              } />

            {/* SaaS Admin - Client sub-routes first */}
              <Route path="/saas-admin/:saasId/clients/create" element={
                <PrivateRoute>
                  <SaaSAdminDashboard view="create-client" />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/clients/admins" element={
                <PrivateRoute>
                  <SaaSAdminDashboard view="client-admins" />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/clients" element={
                <PrivateRoute>
                  <SaaSAdminDashboard view="all-clients" />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/packages" element={
                <PrivateRoute>
                  <PackageManager level="saas" />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/available-packages" element={
                <PrivateRoute>
                  <PackageManager level="saas" view="available" />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/package-usage" element={
                <PrivateRoute>
                  <PackageManager level="saas" view="usage" />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/billing" element={
                <PrivateRoute>
                  <Billing />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/credits" element={
                <PrivateRoute>
                  <Credits />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId/devices" element={
                <PrivateRoute>
                  <Devices />
                </PrivateRoute>
              } />
              <Route path="/saas-admin/:saasId" element={
                <PrivateRoute>
                  <SaaSAdminDashboard />
                </PrivateRoute>
              } />

            {/* Client Admin - Specific routes first */}
              <Route path="/client-admin/:tenantId/packages" element={
                <PrivateRoute>
                  <PackageManager level="tenant" />
                </PrivateRoute>
              } />
              <Route path="/client-admin/:tenantId/available-packages" element={
                <PrivateRoute>
                  <PackageManager level="tenant" view="available" />
                </PrivateRoute>
              } />
              <Route path="/client-admin/:tenantId/package-usage" element={
                <PrivateRoute>
                  <PackageManager level="tenant" view="usage" />
                </PrivateRoute>
              } />
              <Route path="/client-admin/:tenantId/integrations" element={
                <PrivateRoute>
                  <Integrations />
                </PrivateRoute>
              } />
              <Route path="/client-admin/:tenantId/billing" element={
                <PrivateRoute>
                  <Billing />
                </PrivateRoute>
              } />
              <Route path="/client-admin/:tenantId/credits" element={
                <PrivateRoute>
                  <Credits />
                </PrivateRoute>
              } />
              <Route path="/client-admin/:tenantId/devices" element={
                <PrivateRoute>
                  <Devices />
                </PrivateRoute>
              } />
              <Route path="/client-admin/:tenantId" element={
                <PrivateRoute>
                  <ClientAdminDashboard />
                </PrivateRoute>
              } />

            {/* Super Admin */}
              <Route path="/super-admin/add-package" element={
                <PrivateRoute>
                  <PackageManager level="super" view="add" />
                </PrivateRoute>
              } />
              <Route path="/super-admin/package-usage" element={
                <PrivateRoute>
                  <PackageManager level="super" view="usage" />
                </PrivateRoute>
              } />

              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              } />

              <Route path="/content" element={
                <PrivateRoute>
                  <ContentList />
                </PrivateRoute>
              } />

              <Route path="/content/new" element={
                <PrivateRoute>
                  <ContentEditor />
                </PrivateRoute>
              } />

              <Route path="/content/:id" element={
                <PrivateRoute>
                  <ContentEditor />
                </PrivateRoute>
              } />

              <Route path="/admin" element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              } />

              <Route path="/profile" element={
                <PrivateRoute>
                  <UserProfile />
                </PrivateRoute>
              } />

              <Route path="/notifications" element={
                <PrivateRoute>
                  <Notifications />
                </PrivateRoute>
              } />

              <Route path="/settings" element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              } />

              <Route path="/" element={getRootRoute()} />
            </Routes>
          </div>
        </div>
      </div>
    </DomainRouter>
  );
}

export default App;